/* Paw Avenue theme. Vanilla JS with custom elements, no dependencies. */
(() => {
  'use strict';

  const config = window.PawAvenue;
  const { routes, settings, strings } = config;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = document.body.dataset.designMode === 'true';

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const debounce = (fn, ms) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), ms);
    };
  };
  const define = (name, element) => {
    if (!customElements.get(name)) customElements.define(name, element);
  };

  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return config.moneyFormat
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals}}', withCommas.split('.')[0])
      .replace('{{amount}}', withCommas);
  }

  function toast(message) {
    const element = $('#Toast');
    if (!element) return;
    element.textContent = message;
    element.hidden = false;
    clearTimeout(element.timer);
    element.timer = setTimeout(() => {
      element.hidden = true;
    }, 2400);
  }

  /* Scroll reveal --------------------------------------------------------- */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-visible');
              revealObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -6% 0px', threshold: 0.05 }
        )
      : null;

  function initReveal(root = document) {
    $$('[data-reveal]:not(.is-visible)', root).forEach((element) => {
      if (!revealObserver || designMode || reducedMotion.matches) {
        element.classList.add('is-visible');
        return;
      }
      const siblings = Array.from(element.parentElement.children);
      element.style.setProperty('--reveal-delay', `${(siblings.indexOf(element) % 4) * 70}ms`);
      revealObserver.observe(element);
    });
  }

  /* Overlays: drawers, search, quick view --------------------------------- */
  let openOverlays = 0;

  class Overlay extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-close]')) this.close();
      });
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      /* Listens on the document so Escape still works after cart content is re-rendered. */
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) this.close();
      });
    }

    onKeydown(event) {
      if (event.key !== 'Tab') return;
      const focusable = $$('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, summary', this).filter(
        (element) => element.offsetParent !== null
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(opener) {
      if (this.isOpen) return;
      this.opener = opener || document.activeElement;
      this.hidden = false;
      openOverlays += 1;
      document.body.classList.add('has-overlay');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.classList.add('is-open');
          this.focusFirst();
        });
      });
    }

    focusFirst() {
      const target = $('[data-autofocus], input[type="search"], [data-close]:not(.overlay-backdrop)', this);
      if (target) target.focus({ preventScroll: true });
    }

    async close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      openOverlays = Math.max(0, openOverlays - 1);
      if (!openOverlays) document.body.classList.remove('has-overlay');
      if (this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
      await wait(reducedMotion.matches ? 0 : 340);
      if (!this.isOpen) this.hidden = true;
    }
  }

  class SideDrawer extends Overlay {}

  class CartDrawer extends Overlay {}

  class SearchOverlay extends Overlay {
    connectedCallback() {
      super.connectedCallback();
      this.input = $('input[type="search"]', this);
      this.results = $('[data-results]', this);
      this.input.addEventListener(
        'input',
        debounce(() => this.search(), 220)
      );
    }

    async search() {
      const terms = this.input.value.trim();
      if (!terms) {
        this.results.innerHTML = '';
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q: terms,
        'resources[type]': 'product,collection,page',
        'resources[limit]': '6',
        section_id: 'predictive-search',
      });
      try {
        const response = await fetch(`${routes.searchSuggest}?${params}`, { signal: this.controller.signal });
        if (!response.ok) return;
        const content = $('.shopify-section', parseHTML(await response.text()));
        this.results.innerHTML = content ? content.innerHTML : '';
      } catch (error) {
        if (error.name !== 'AbortError') this.results.innerHTML = '';
      }
    }
  }

  class QuickView extends Overlay {
    async load(url, opener) {
      const content = $('[data-content]', this);
      content.innerHTML = '<div class="quick-view__loading"><span class="spinner"></span></div>';
      this.open(opener);
      try {
        const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=quick-view`);
        if (!response.ok) throw new Error('quick view failed');
        const section = $('.shopify-section', parseHTML(await response.text()));
        content.innerHTML = section ? section.innerHTML : '';
      } catch (error) {
        window.location.href = url;
      }
    }
  }

  const cartDrawer = () => $('#CartDrawer');
  const usesDrawer = () => settings.cartType === 'drawer' && !!cartDrawer();

  /* Fly to cart ----------------------------------------------------------- */
  const PAW_SVG =
    '<svg viewBox="0 0 100 100" fill="currentColor" aria-hidden="true"><ellipse cx="22" cy="42" rx="9" ry="12" transform="rotate(-25 22 42)"/><ellipse cx="40" cy="22" rx="10" ry="13" transform="rotate(-8 40 22)"/><ellipse cx="60" cy="22" rx="10" ry="13" transform="rotate(8 60 22)"/><ellipse cx="78" cy="42" rx="9" ry="12" transform="rotate(25 78 42)"/><path d="M50 46c12 0 22 12 28 22 6 10 0 22-12 20-6-1-10-4-16-4s-10 3-16 4c-12 2-18-10-12-20 6-10 16-22 28-22Z"/></svg>';

  const inViewport = (rect) =>
    rect.width > 0 && rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;

  function findMedia(trigger) {
    if (!trigger) return null;
    const scopes = [
      trigger.closest('.card'),
      trigger.closest('.upsell'),
      trigger.closest('sticky-buy'),
      trigger.closest('.quick-view__grid'),
      trigger.closest('.shopify-section'),
    ];
    for (const scope of scopes) {
      if (!scope) continue;
      const media = $$('[data-product-media], .upsell__media', scope).find((element) => inViewport(element.getBoundingClientRect()));
      if (media) return media;
    }
    return null;
  }

  function dropPaw(x, y, angle) {
    const paw = document.createElement('span');
    paw.className = 'fly-paw';
    paw.innerHTML = PAW_SVG;
    paw.style.left = `${x}px`;
    paw.style.top = `${y}px`;
    document.body.appendChild(paw);
    paw
      .animate(
        [
          { opacity: 0, transform: `translate(-50%, -50%) rotate(${angle}deg) scale(.4)` },
          { opacity: 0.95, transform: `translate(-50%, -50%) rotate(${angle}deg) scale(1)`, offset: 0.2 },
          { opacity: 0, transform: `translate(-50%, -50%) rotate(${angle}deg) scale(.9)` },
        ],
        { duration: 900, easing: 'ease-out' }
      )
      .finished.finally(() => paw.remove());
  }

  async function flyToCart(media, startRect) {
    const target = $('#HeaderCart');
    if (!settings.flyToCart || reducedMotion.matches || !target || !startRect) return;
    const end = target.getBoundingClientRect();
    if (!end.width) return;

    const size = Math.max(64, Math.min(170, Math.min(startRect.width, startRect.height)));
    const startX = Math.min(window.innerWidth - size / 2, Math.max(size / 2, startRect.left + startRect.width / 2));
    const startY = Math.min(window.innerHeight - size / 2, Math.max(size / 2, startRect.top + startRect.height / 2));
    const endX = end.left + end.width / 2;
    const endY = end.top + end.height / 2;
    const arc = Math.min(180, Math.abs(startY - endY) * 0.35 + 70);

    const flyer = document.createElement('div');
    flyer.className = 'fly-item';
    flyer.style.width = flyer.style.height = `${size}px`;
    flyer.style.left = `${startX - size / 2}px`;
    flyer.style.top = `${startY - size / 2}px`;
    const image = media ? $('img', media) : null;
    if (image) {
      const clone = new Image();
      clone.src = image.currentSrc || image.src;
      clone.alt = '';
      flyer.appendChild(clone);
    } else {
      flyer.innerHTML = PAW_SVG;
    }
    document.body.appendChild(flyer);

    const point = (t) => ({
      x: (endX - startX) * t,
      y: (endY - startY) * t - Math.sin(Math.PI * t) * arc,
    });
    const steps = 16;
    const frames = [];
    for (let step = 0; step <= steps; step += 1) {
      const t = step / steps;
      const { x, y } = point(t);
      const lift = t < 0.14 ? 1 + t * 0.6 : 1.08 - (t - 0.14) * 1.1;
      frames.push({
        transform: `translate(${x}px, ${y}px) rotate(${t * -16}deg) scale(${Math.max(0.14, lift)})`,
        borderRadius: `${18 + t * 82}%`,
        opacity: t > 0.92 ? 0.7 : 1,
      });
    }

    const duration = 820;
    if (settings.pawTrail) {
      [0.2, 0.38, 0.56, 0.74].forEach((t) => {
        setTimeout(() => {
          const current = point(t);
          const next = point(t + 0.04);
          const angle = (Math.atan2(next.y - current.y, next.x - current.x) * 180) / Math.PI + 90;
          dropPaw(startX + current.x, startY + current.y, angle);
        }, duration * t);
      });
    }

    await flyer.animate(frames, { duration, easing: 'cubic-bezier(.45,.05,.3,1)', fill: 'forwards' }).finished.catch(() => {});
    flyer.remove();
  }

  function bumpCart() {
    const target = $('#HeaderCart');
    if (!target) return;
    target.classList.remove('is-bumping');
    void target.offsetWidth;
    target.classList.add('is-bumping');
  }

  /* Cart ------------------------------------------------------------------ */
  function cartSectionIds() {
    const ids = [];
    if (cartDrawer()) ids.push('cart-drawer');
    const page = $('cart-page');
    if (page) ids.push(page.dataset.section);
    return ids;
  }

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((element) => {
      element.textContent = count;
      element.hidden = count === 0;
    });
  }

  function renderCartSections(sections) {
    if (!sections) return;
    let count = null;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const fresh = $('[data-cart-content]', parseHTML(html));
      if (!fresh) return;
      const target = id === 'cart-drawer' ? $('#CartDrawer [data-cart-content]') : $('cart-page [data-cart-content]');
      if (target) target.innerHTML = fresh.innerHTML;
      if (id === 'cart-drawer' && cartDrawer().isOpen) cartDrawer().focusFirst();
      if (fresh.dataset.cartTotal !== undefined) count = Number(fresh.dataset.cartTotal);
    });
    return count;
  }

  async function addToCart(formData, trigger) {
    const media = findMedia(trigger);
    const startRect = media ? media.getBoundingClientRect() : trigger ? trigger.getBoundingClientRect() : null;
    const ids = cartSectionIds();
    if (ids.length) {
      formData.append('sections', ids.join(','));
      formData.append('sections_url', window.location.pathname);
    }
    const response = await fetch(routes.cartAdd, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || strings.error);

    const quickView = $('#QuickView');
    const flight = flyToCart(media, startRect);
    if (quickView && quickView.isOpen) quickView.close();
    const count = renderCartSections(data.sections);

    /* The drawer starts sliding in as the item reaches the cart icon. */
    await Promise.race([flight, wait(settings.flyToCart && !reducedMotion.matches ? 640 : 0)]);
    if (count !== null && count !== undefined) updateCartCount(count);
    bumpCart();
    if (usesDrawer()) {
      cartDrawer().open(trigger);
    } else {
      toast(strings.added);
    }
    await flight;
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  async function changeLine(line, quantity, row) {
    const ids = cartSectionIds();
    if (row) row.classList.add(quantity === 0 ? 'is-removing' : 'is-loading');
    if (quantity === 0 && !reducedMotion.matches) await wait(220);
    try {
      const response = await fetch(routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: ids, sections_url: window.location.pathname }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || strings.error);
      renderCartSections(data.sections);
      updateCartCount(data.item_count);
    } catch (error) {
      if (row) row.classList.remove('is-removing', 'is-loading');
      $$('[data-cart-error]').forEach((element) => {
        element.textContent = error.message;
        element.hidden = false;
      });
    }
  }

  const changeLineDebounced = debounce((input) => {
    const row = input.closest('.cart-line');
    if (row) changeLine(Number(row.dataset.line), Math.max(0, Number(input.value) || 0), row);
  }, 280);

  /* Quantity -------------------------------------------------------------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-step]');
        if (!button) return;
        const input = $('input', this);
        const min = Number(input.min) || 0;
        input.value = Math.max(min, (Number(input.value) || 0) + Number(button.dataset.step));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  /* Product forms --------------------------------------------------------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      this.button = $('[data-add-button]', this);
      this.error = $('[data-error]', this);
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.button || this.button.disabled || this.button.classList.contains('is-loading')) return;
      this.error.hidden = true;
      this.button.classList.add('is-loading');
      try {
        const trigger = this.lastTrigger || this.button;
        await addToCart(new FormData(this.form), trigger);
      } catch (error) {
        this.error.textContent = error.message || strings.error;
        this.error.hidden = false;
      } finally {
        this.button.classList.remove('is-loading');
        this.lastTrigger = null;
      }
    }
  }

  class QuickAdd extends HTMLElement {
    connectedCallback() {
      this.button = $('button', this);
      this.button.addEventListener('click', () => this.onClick());
    }

    async onClick() {
      if (this.dataset.productUrl) {
        $('#QuickView').load(this.dataset.productUrl, this.button);
        return;
      }
      if (this.button.classList.contains('is-loading')) return;
      this.button.classList.add('is-loading');
      const formData = new FormData();
      formData.append('id', this.dataset.variantId);
      formData.append('quantity', '1');
      try {
        await addToCart(formData, this.button);
      } catch (error) {
        toast(error.message || strings.error);
      } finally {
        if (this.isConnected) this.button.classList.remove('is-loading');
      }
    }
  }

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse($('[data-variants]', this).textContent);
      this.fieldsets = $$('fieldset', this);
      this.addEventListener('change', () => this.onChange());
      this.markAvailability();
    }

    selectedOptions() {
      return this.fieldsets.map((fieldset) => {
        const checked = $('input:checked', fieldset);
        return checked ? checked.value : null;
      });
    }

    onChange() {
      const options = this.selectedOptions();
      const variant = this.variants.find((item) => item.options.every((value, index) => value === options[index]));
      this.fieldsets.forEach((fieldset, index) => {
        const label = $('[data-option-value]', fieldset);
        if (label) label.textContent = options[index];
      });
      this.markAvailability();
      this.apply(variant);
    }

    markAvailability() {
      const options = this.selectedOptions();
      this.fieldsets.forEach((fieldset, index) => {
        $$('input', fieldset).forEach((input) => {
          const match = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options[index] === input.value &&
              variant.options.every((value, position) => position === index || value === options[position])
          );
          input.classList.toggle('is-unavailable', !match);
        });
      });
    }

    apply(variant) {
      const form = document.getElementById(this.dataset.formId);
      const scope = this.closest('[data-product-scope]');
      if (!form) return;
      const idInput = $('input[name="id"]', form);
      const button = $('[data-add-button]', form);
      const label = $('[data-add-label]', form);
      const sticky = $(`sticky-buy[data-form-id="${this.dataset.formId}"]`);
      const available = !!(variant && variant.available);
      const text = !variant ? strings.unavailable : available ? strings.addToCart : strings.soldOut;

      if (variant) idInput.value = variant.id;
      button.disabled = !available;
      label.textContent = text;

      if (variant) {
        const markup = this.priceMarkup(variant);
        if (scope) $$('[data-price]', scope).forEach((element) => (element.innerHTML = markup));
        if (sticky) {
          const price = $('[data-sticky-price] [data-price]', sticky);
          if (price) price.innerHTML = markup;
        }
        if (variant.featured_media) {
          const gallery = $('product-gallery');
          if (gallery && scope && scope.classList.contains('product__info')) gallery.show(variant.featured_media.id);
        }
        if (scope && scope.classList.contains('product__info')) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        }
      }
      if (sticky) {
        const stickyButton = $('[data-sticky-add]', sticky);
        stickyButton.disabled = !available;
        $('[data-add-label]', stickyButton).textContent = text;
      }
    }

    priceMarkup(variant) {
      const now = formatMoney(variant.price);
      if (variant.compare_at_price > variant.price) {
        return `<span class="price__now price__now--sale">${now}</span><s class="price__was">${formatMoney(variant.compare_at_price)}</s>`;
      }
      return `<span class="price__now">${now}</span>`;
    }
  }

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.slider = $('[data-slider]', this);
      this.slides = $$('.gallery__slide', this);
      this.thumbs = $$('.gallery__thumb', this);
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.show(thumb.dataset.thumb)));
      this.slider.addEventListener(
        'scroll',
        debounce(() => this.syncFromScroll(), 80),
        { passive: true }
      );
      this.slider.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const next = this.currentIndex() + (event.key === 'ArrowRight' ? 1 : -1);
        const slide = this.slides[Math.max(0, Math.min(this.slides.length - 1, next))];
        this.show(slide.dataset.mediaId);
      });
      if (window.matchMedia('(hover: hover)').matches) {
        $$('.gallery__zoom', this).forEach((zoom) => {
          zoom.addEventListener('mousemove', (event) => {
            const box = zoom.getBoundingClientRect();
            zoom.style.setProperty('--zoom-x', `${((event.clientX - box.left) / box.width) * 100}%`);
            zoom.style.setProperty('--zoom-y', `${((event.clientY - box.top) / box.height) * 100}%`);
          });
        });
      }
      const initial = this.slides.find((slide) => slide.hasAttribute('data-product-media'));
      if (initial && initial !== this.slides[0]) this.show(initial.dataset.mediaId, false);
    }

    currentIndex() {
      return Math.round(this.slider.scrollLeft / Math.max(1, this.slider.clientWidth));
    }

    show(id, smooth = true) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(id));
      if (index < 0) return;
      this.slider.scrollTo({ left: this.slider.clientWidth * index, behavior: smooth && !reducedMotion.matches ? 'smooth' : 'auto' });
      this.mark(index);
    }

    syncFromScroll() {
      this.mark(this.currentIndex());
    }

    mark(index) {
      this.slides.forEach((slide, position) => slide.toggleAttribute('data-product-media', position === index));
      this.thumbs.forEach((thumb, position) => thumb.classList.toggle('is-active', position === index));
      const active = this.thumbs[index];
      if (active) active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  class StickyBuy extends HTMLElement {
    connectedCallback() {
      const mainButton = $('#MainAddButton');
      const form = document.getElementById(this.dataset.formId);
      if (!mainButton || !form) return;
      const button = $('[data-sticky-add]', this);
      button.addEventListener('click', () => {
        const productForm = form.closest('product-form');
        productForm.lastTrigger = button;
        form.requestSubmit();
      });
      new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.hidden = false;
        this.classList.toggle('is-visible', passed);
        document.body.classList.toggle('has-sticky-buy', passed);
      }).observe(mainButton);
    }
  }

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((html) => {
          const fresh = $('product-recommendations', parseHTML(html));
          if (fresh && fresh.innerHTML.trim()) {
            this.innerHTML = fresh.innerHTML;
            initReveal(this);
          }
        })
        .catch(() => {});
    }
  }

  /* Header ---------------------------------------------------------------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      if (!settings.shrinkHeader) return;
      const update = () => this.classList.toggle('is-scrolled', window.scrollY > 40);
      window.addEventListener('scroll', update, { passive: true });
      update();
    }
  }

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = $$('.announcement__item', this);
      if (this.items.length < 2 || designMode || reducedMotion.matches) return;
      let index = 0;
      this.timer = setInterval(() => {
        this.items[index].classList.remove('is-active');
        index = (index + 1) % this.items.length;
        this.items[index].classList.add('is-active');
      }, (Number(this.dataset.speed) || 5) * 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }

  class CountDown extends HTMLElement {
    connectedCallback() {
      this.end = new Date(this.dataset.end.trim().replace(' ', 'T')).getTime();
      if (Number.isNaN(this.end)) return;
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const left = this.end - Date.now();
      if (left <= 0) {
        this.hidden = true;
        clearInterval(this.timer);
        return;
      }
      this.hidden = false;
      const set = (name, value) => {
        $(`[data-${name}]`, this).textContent = String(value).padStart(2, '0');
      };
      set('days', Math.floor(left / 86400000));
      set('hours', Math.floor(left / 3600000) % 24);
      set('minutes', Math.floor(left / 60000) % 60);
      set('seconds', Math.floor(left / 1000) % 60);
    }
  }

  /* Collection filters ---------------------------------------------------- */
  class FacetForm extends HTMLElement {
    connectedCallback() {
      this.addEventListener(
        'change',
        debounce(() => this.apply(), 320)
      );
      this.addEventListener('submit', (event) => {
        event.preventDefault();
        this.apply();
      });
      this.addEventListener('click', (event) => {
        const panel = $('[data-filter-panel]', this);
        if (event.target.closest('[data-open-filters]')) {
          panel.classList.add('is-open');
          document.body.classList.add('has-overlay');
        }
        if (event.target.closest('[data-close-filters]')) {
          panel.classList.remove('is-open');
          document.body.classList.remove('has-overlay');
        }
      });
    }

    async apply() {
      const form = $('form', this);
      const panelOpen = $('[data-filter-panel]', this).classList.contains('is-open');
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
      const url = `${form.getAttribute('action')}?${params}`;
      const listing = this.closest('[data-listing]');
      listing.classList.add('is-loading');
      try {
        const response = await fetch(`${url}&section_id=${this.dataset.section}`);
        const fresh = $('[data-listing]', parseHTML(await response.text()));
        if (!fresh) throw new Error('filter failed');
        listing.innerHTML = fresh.innerHTML;
        if (panelOpen) $('[data-filter-panel]', listing).classList.add('is-open');
        window.history.replaceState({}, '', url);
        initReveal(listing);
      } catch (error) {
        window.location.href = url;
      } finally {
        listing.classList.remove('is-loading');
      }
    }
  }

  /* Register elements ----------------------------------------------------- */
  define('side-drawer', SideDrawer);
  define('cart-drawer', CartDrawer);
  define('search-overlay', SearchOverlay);
  define('quick-view', QuickView);
  define('quantity-input', QuantityInput);
  define('product-form', ProductForm);
  define('quick-add', QuickAdd);
  define('variant-picker', VariantPicker);
  define('product-gallery', ProductGallery);
  define('sticky-buy', StickyBuy);
  define('product-recommendations', ProductRecommendations);
  define('sticky-header', StickyHeader);
  define('announcement-bar', AnnouncementBar);
  define('count-down', CountDown);
  define('facet-form', FacetForm);

  /* Page level events ----------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const cartLink = event.target.closest('[data-open-cart]');
    if (cartLink && usesDrawer()) {
      event.preventDefault();
      cartDrawer().open(cartLink);
      return;
    }
    const searchButton = event.target.closest('[data-open-search]');
    if (searchButton) {
      $('#SearchOverlay').open(searchButton);
      return;
    }
    const menuButton = event.target.closest('[data-open-menu]');
    if (menuButton) {
      $('#MobileMenu').open(menuButton);
      return;
    }
    const removeButton = event.target.closest('[data-remove]');
    if (removeButton) {
      const row = removeButton.closest('.cart-line');
      changeLine(Number(row.dataset.line), 0, row);
      return;
    }
    const shareButton = event.target.closest('[data-share]');
    if (shareButton) {
      const { url, title } = shareButton.dataset;
      if (navigator.share) {
        navigator.share({ url, title }).catch(() => {});
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(() => toast(strings.linkCopied));
      }
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-line-quantity]')) changeLineDebounced(event.target);
    if (event.target.matches('[data-cart-note]')) {
      fetch(routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: event.target.value }),
      }).catch(() => {});
    }
  });

  document.addEventListener('shopify:section:load', (event) => initReveal(event.target));

  initReveal();
})();
